// src/data/garde-meubles-communes.ts
//
// Garde-meubles : déclinaisons communales — LOT 7 du plan SEO.
//
// Le garde-meubles est à Seraing (registre : lieux.garde_meubles). Ces pages
// décrivent une DESSERTE, pas un dépôt local, et la règle d'honnêteté est
// appliquée par le validateur : hors Seraing, aucune formulation « box à
// <commune> », « dépôt à <commune> », « garde-meubles à <commune> ».
//
// L'AVERTISSEMENT du plan : le site a déjà perdu l'indexation de pages
// communes trop proches. Le critère de réussite est le ratio contenu unique /
// gabarit. Ici, tout ce qui est propre à la commune vient de sa fiche (bâti,
// relief, procédure) et rien de ce qui est commun n'est répété dans les
// données — le gabarit React porte les faits communs (tarifs, assurance,
// durée) une seule fois, via le registre. Le contrôle 12h du build mesure la
// similarité entre les pages servies et refuse deux pages trop proches.
//
// Publication échelonnée : `vague` 1, 2, 3 ; le validateur refuse d'ouvrir
// une vague tant que la précédente n'est pas entièrement publiable.
import donnees from './garde-meubles-communes.json';
import { COMMUNES, type CommuneSEO } from './communes';

export const RACINE_GARDE_MEUBLES = '/garde-meubles';

export interface PageGardeMeublesCommune {
  slug: string;
  vague: 1 | 2 | 3;
  requete: string;
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  /** Vrai pour Seraing seulement : le garde-meubles est dans la commune. */
  memeCommune: boolean;
  distanceGardeMeublesKm: number | null;
  tempsGardeMeublesMin: number | null;
  titreMeta: string;
  descriptionMeta: string;
  h1: string;
  capsule: string;
  contexte: Array<{ titre: string; texte: string }>;
  enlevement: string;
  aRetenir: string;
  faq: Array<{ q: string; a: string }>;
  aValider: string[];
}

interface Donnees {
  pages: PageGardeMeublesCommune[];
}

const D = donnees as unknown as Donnees;

export const PAGES_GARDE_MEUBLES: readonly PageGardeMeublesCommune[] = D.pages;

/** URL d'une déclinaison : /garde-meubles/garde-meubles-<slug>, comme la page Liège. */
export function cheminGardeMeublesCommune(slug: string): string {
  return `${RACINE_GARDE_MEUBLES}/garde-meubles-${slug}`;
}

/** Le slug de commune porté par un segment « garde-meubles-<slug> », sinon undefined. */
export function slugGardeMeublesDepuisSegment(segment: string | undefined): string | undefined {
  if (!segment) return undefined;
  const m = /^garde-meubles-([a-z0-9-]+)$/.exec(segment);
  return m ? m[1] : undefined;
}

export function pageGardeMeublesParSlug(slug: string | undefined): PageGardeMeublesCommune | undefined {
  return slug ? PAGES_GARDE_MEUBLES.find((p) => p.slug === slug) : undefined;
}

export function communeDe(page: PageGardeMeublesCommune): CommuneSEO | undefined {
  return COMMUNES.find((c) => c.id === page.slug);
}

/** La fiche est-elle relue et complète ? Distance obligatoire hors Seraing. */
export function pageGardeMeublesVerifiee(p: PageGardeMeublesCommune): boolean {
  const distanceOk = p.memeCommune || (p.distanceGardeMeublesKm !== null && p.tempsGardeMeublesMin !== null);
  return p.verifiePar === 'human' && Boolean(p.dateVerification) && distanceOk;
}

/** « 2026-09-06 » → « septembre 2026 ». */
export function moisAnnee(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
}

export interface ProblemeGardeMeubles {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

/**
 * Formulations qui laisseraient croire à un dépôt dans la commune :
 * « box à Verviers », « dépôt de Huy ». Une NÉGATION dit exactement le
 * contraire — « pas de dépôt à Verviers », « aucun box à Huy » — et c'est ce
 * que la page doit dire : elle est admise.
 */
export function collocationTrompeuse(texte: string, nom: string): string | null {
  const n = nom.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(box|dépôt|depot|entrepôt|entrepot|garde-meubles?|site de stockage)\\s+(à|a|de|d')\\s*${n}\\b`, 'gi');
  for (const m of texte.matchAll(re)) {
    const avant = texte.slice(Math.max(0, (m.index ?? 0) - 24), m.index).toLowerCase();
    if (/(pas d[e']\s*|aucun[e]?\s+|ni\s+|sans\s+)$/.test(avant)) continue;
    return m[0];
  }
  return null;
}

export function validerGardeMeublesCommunes(aujourdhui: Date = new Date()): ProblemeGardeMeubles[] {
  const p: ProblemeGardeMeubles[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null) => Boolean(iso) && new Date(iso as string) < limite;

  const slugs = PAGES_GARDE_MEUBLES.map((x) => x.slug);
  for (const s of new Set(slugs.filter((x, i) => slugs.indexOf(x) !== i))) {
    p.push({ ou: s, gravite: 'erreur', message: 'slug présent plusieurs fois' });
  }

  const questions = new Map<string, string>();
  const publiablesParVague = new Map<number, boolean>();

  for (const page of PAGES_GARDE_MEUBLES) {
    const ou = `pages.${page.slug}`;
    const commune = communeDe(page);
    if (!commune) { p.push({ ou, gravite: 'erreur', message: 'commune inconnue de communes.json' }); continue; }
    if (commune.statut !== 'published') p.push({ ou, gravite: 'erreur', message: 'la page déménagement de la commune n\'est pas publiée' });
    if (page.memeCommune && page.slug !== 'seraing') p.push({ ou, gravite: 'erreur', message: 'memeCommune ne vaut que pour Seraing, où est le garde-meubles' });

    if (page.verifiePar !== 'human' && page.verifiePar !== null) p.push({ ou, gravite: 'erreur', message: `verifiePar vaut « ${page.verifiePar} » — attendu "human" ou null` });
    if (page.publiable && !pageGardeMeublesVerifiee(page)) p.push({ ou, gravite: 'erreur', message: 'publiable=true mais page non relue ou sans distance vers Seraing' });
    if (page.verifiePar === null) p.push({ ou, gravite: 'avertissement', message: 'page collectée mais non relue (verifiePar: null) — marqueur « À VALIDER » rendu' });
    if (perimee(page.dateVerification)) p.push({ ou, gravite: 'avertissement', message: `page relue il y a plus de 12 mois (${page.dateVerification})` });

    if (page.titreMeta.length > 60) p.push({ ou, gravite: 'erreur', message: `titreMeta de ${page.titreMeta.length} caractères (max 60)` });
    if (page.descriptionMeta.length > 165) p.push({ ou, gravite: 'erreur', message: `descriptionMeta de ${page.descriptionMeta.length} caractères (max 165)` });
    const mots = page.capsule.split(/\s+/).length;
    if (mots < 40 || mots > 75) p.push({ ou, gravite: 'erreur', message: `capsule de ${mots} mots (viser 50 à 60)` });
    if (page.contexte.length < 2) p.push({ ou, gravite: 'erreur', message: 'moins de deux blocs de contexte local' });
    if (page.faq.length < 3 || page.faq.length > 5) p.push({ ou, gravite: 'erreur', message: `faq : ${page.faq.length} question(s) — il en faut entre 3 et 5` });

    // Honnêteté : hors Seraing, rien ne laisse croire à un dépôt local.
    if (!page.memeCommune) {
      const textes = [page.h1, page.titreMeta, page.descriptionMeta, page.capsule, page.enlevement, page.aRetenir,
        ...page.contexte.flatMap((c) => [c.titre, c.texte]), ...page.faq.flatMap((f) => [f.q, f.a])];
      for (const t of textes) {
        const m = collocationTrompeuse(t, commune.nom);
        if (m) p.push({ ou, gravite: 'erreur', message: `formulation qui laisse croire à un dépôt local : « ${m} »` });
      }
      if (!/Seraing/.test(page.h1) && !/Seraing/.test(page.capsule)) p.push({ ou, gravite: 'erreur', message: 'le H1 ou la capsule doit situer les box à Seraing' });
      if (!new RegExp(`pour ${commune.nom}`).test(page.h1)) p.push({ ou, gravite: 'erreur', message: `le H1 dit « pour ${commune.nom} », pas « à ${commune.nom} »` });
    }

    // FAQ propre à chaque page : une question ne se répète pas d'une commune à l'autre.
    for (const f of page.faq) {
      const cle = f.q.trim().toLowerCase();
      const deja = questions.get(cle);
      if (deja) p.push({ ou, gravite: 'erreur', message: `question de FAQ déjà posée sur ${deja} : « ${f.q} »` });
      else questions.set(cle, page.slug);
    }

    publiablesParVague.set(page.vague, (publiablesParVague.get(page.vague) ?? true) && page.publiable);
  }

  // Publication échelonnée : une vague ne s'ouvre pas avant la précédente.
  for (const page of PAGES_GARDE_MEUBLES) {
    if (!page.publiable) continue;
    for (let v = 1; v < page.vague; v++) {
      if (publiablesParVague.has(v) && !publiablesParVague.get(v)) {
        p.push({ ou: `pages.${page.slug}`, gravite: 'erreur', message: `vague ${page.vague} publiée alors que la vague ${v} ne l'est pas entièrement` });
      }
    }
  }
  return p;
}
